// Tùy biến KHCN: nhận sự kiện thời gian thực (bình luận, bình chọn, đang nhập) qua Server-Sent Events.
//
// Chỉ mở MỘT kết nối EventSource dùng chung cho cả trang, bất kể có bao nhiêu thành phần đang nghe.
// HTTP/1.1 giới hạn ~6 kết nối đồng thời mỗi origin; nếu mỗi thành phần mở một kết nối SSE riêng thì
// chỉ vài lần điều hướng là cạn pool và mọi request khác (tải bài, ảnh) bị treo. Kết nối dùng chung
// nhận mọi sự kiện của tenant, rồi lọc theo bài ở phía trình duyệt.

export type RealtimeEventType = "comment" | "vote" | "typing"

export interface RealtimeEvent {
  type: RealtimeEventType
  postNumber: number
  data?: Record<string, unknown>
  at: string
}

const EVENT_TYPES: RealtimeEventType[] = ["comment", "vote", "typing"]

interface Listener {
  postNumber: number | undefined // undefined = nhận mọi bài
  onEvent: (event: RealtimeEvent) => void
}

const listeners = new Set<Listener>()
let source: EventSource | undefined
let closeTimer: number | undefined

// Trạng thái kết nối để hiển thị cho quản trị viên trên header
export type RealtimeStatus = "connecting" | "open" | "closed"
let status: RealtimeStatus = "closed"
const statusListeners = new Set<(s: RealtimeStatus) => void>()

const setStatus = (next: RealtimeStatus) => {
  if (status === next) {
    return
  }
  status = next
  statusListeners.forEach((cb) => cb(next))
}

export const getRealtimeStatus = (): RealtimeStatus => status

export const onRealtimeStatusChange = (cb: (s: RealtimeStatus) => void): (() => void) => {
  statusListeners.add(cb)
  return () => statusListeners.delete(cb)
}

const isSupported = () => typeof window !== "undefined" && typeof window.EventSource !== "undefined"

const dispatch = (raw: Event) => {
  let event: RealtimeEvent
  try {
    event = JSON.parse((raw as MessageEvent).data) as RealtimeEvent
  } catch {
    return
  }
  listeners.forEach((listener) => {
    if (listener.postNumber === undefined || listener.postNumber === event.postNumber) {
      listener.onEvent(event)
    }
  })
}

const openConnection = () => {
  if (source || !isSupported()) {
    return
  }
  // Một kết nối cho toàn bộ sự kiện của tenant; lọc theo bài ở client
  setStatus("connecting")
  source = new window.EventSource("/api/v1/events")
  source.onopen = () => setStatus("open")
  // EventSource tự kết nối lại khi lỗi; coi như đang kết nối lại
  source.onerror = () => setStatus(source && source.readyState === 2 ? "closed" : "connecting")
  EVENT_TYPES.forEach((type) => source?.addEventListener(type, dispatch))
}

const closeConnection = () => {
  if (source) {
    source.close()
    source = undefined
  }
  setStatus("closed")
}

// Đăng ký nhận sự kiện; postNumber bỏ trống = mọi bài (trang chủ). Trả về hàm hủy.
export const subscribeToEvents = (postNumber: number | undefined, onEvent: (event: RealtimeEvent) => void): (() => void) => {
  if (!isSupported()) {
    return () => undefined
  }
  const listener: Listener = { postNumber, onEvent }
  listeners.add(listener)
  if (closeTimer) {
    window.clearTimeout(closeTimer)
    closeTimer = undefined
  }
  openConnection()

  return () => {
    listeners.delete(listener)
    // Không còn ai nghe thì đóng kết nối sau một nhịp ngắn (tránh đóng/mở liên tục khi chuyển trang)
    if (listeners.size === 0) {
      closeTimer = window.setTimeout(() => {
        if (listeners.size === 0) {
          closeConnection()
        }
      }, 2000)
    }
  }
}
